import {
  playerApi,
  type PlayerPartyInvite,
  type PlayerSocialState,
} from '../api/public.ts'
import {
  applyPresenceChanged,
  compareDecimalIDs,
  type PlayerChatChangedHint,
} from './playerSocialPresentation.ts'
import {
  createPlayerSocialDockSession,
  type PlayerSocialDockOptions,
} from './playerSocialDockRuntime.ts'
import { createPlayerActivityReconcileQueue } from './playerActivityReconcile.ts'
import { usePlayerSocialDockChat } from './usePlayerSocialDockChat.ts'
import { usePlayerSocialDockParty } from './usePlayerSocialDockParty.ts'
import { usePlayerSocialDockPresence } from './usePlayerSocialDockPresence.ts'
import { onScopeDispose, ref, watch } from 'vue'

export function usePlayerSocialDock(options: PlayerSocialDockOptions) {
  const socialState = ref<PlayerSocialState | null>(null)
  const socialLoading = ref(false)
  const socialActionPending = ref(false)
  const socialError = ref<string | null>(null)

  let sessionGeneration = 0
  let socialRequestID = 0
  let socialActionRequestID = 0
  let foregroundActivationGeneration = 0
  let foregroundActive = false
  let foregroundRefreshRequested: {
    key: string
    activationGeneration: number
  } | null = null
  let foregroundRefreshCompletedKey = ''
  let foregroundRefreshInFlight: {
    key: string
    promise: Promise<void>
  } | null = null
  let friendRequestNotificationsReady = false
  let partyInviteNotificationsReady = false
  let chatMessageNotificationsReady = false
  let pendingSocialNotificationObservedAt: number | undefined
  let knownIncomingFriendRequestIDs = new Set<string>()
  let knownIncomingPartyInviteIDs = new Set<string>()
  const seenIncomingChatMessageKeys = new Set<string>()
  const session = createPlayerSocialDockSession(
    options,
    () => sessionGeneration,
  )

  let chatRuntime!: ReturnType<typeof usePlayerSocialDockChat>
  const partyRuntime = usePlayerSocialDockParty({
    session,
    refreshChat: forceReplace => chatRuntime.refreshChat(forceReplace),
    invalidatePartyChat: notice => chatRuntime.invalidatePartyChat(notice),
    onMembershipChanged: (previousMembership, nextMembership) => {
      chatRuntime.handlePartyMembershipChanged(
        previousMembership,
        nextMembership,
      )
    },
    onIncomingInvitesCommitted: handleIncomingPartyInvitesCommitted,
  })
  chatRuntime = usePlayerSocialDockChat({
    session,
    party: partyRuntime.party,
    partyVersion: partyRuntime.partyVersion,
    partyStateHealthy: partyRuntime.partyStateHealthy,
    socialState,
    socialActionPending,
    refreshParty: partyRuntime.refreshParty,
    isForegroundActive: () => foregroundActive,
  })

  let activityReconcileQueue!: ReturnType<
    typeof createPlayerActivityReconcileQueue
  >
  const presenceRuntime = usePlayerSocialDockPresence({
    session,
    onStreamOpen: connectionCycle => {
      if (!foregroundActive) return
      if (connectionCycle > 0) suspendIncomingNotifications()
      void requestForegroundRefresh(
        foregroundActivationGeneration,
        connectionCycle,
      )
    },
    onStreamInterrupted: suspendIncomingNotifications,
    onSocialSnapshot: state => {
      activityReconcileQueue.reset()
      socialRequestID += 1
      commitSocialState(
        state,
        pendingSocialNotificationObservedAt,
      )
      socialLoading.value = false
      socialError.value = null
    },
    onSocialChanged: observedAt => {
      pendingSocialNotificationObservedAt = earliestObservedAt(
        pendingSocialNotificationObservedAt,
        observedAt,
      )
      void refreshSocial()
    },
    onActivityChanged: () => {
      activityReconcileQueue.request()
    },
    onPresenceChanged: envelope => {
      const currentState = socialState.value
      if (!currentState) {
        void refreshSocial()
        return
      }
      const next = applyPresenceChanged(
        currentState,
        envelope,
        options.steamID(),
      )
      if (next && next !== currentState) {
        socialRequestID += 1
        socialState.value = next
        socialLoading.value = false
      } else if (!next) {
        void refreshSocial()
      }
    },
    onPartyChanged: observedAt => {
      void partyRuntime.refreshParty(false, observedAt).then(() => {
        options.onPartyChanged()
        return chatRuntime.refreshChat()
      })
    },
    onChatChanged: (hint, observedAt) => {
      notifyIncomingChatMessage(hint, observedAt)
      chatRuntime.queueRefreshHint(hint)
    },
    onCommunicationPolicyChanged: observedAt => {
      options.onCommunicationPolicyChanged?.(observedAt)
    },
    onPageHide: () => {
      deactivateForeground()
      partyRuntime.clearRetryTimer()
      chatRuntime.clearRefreshTimers()
    },
    onPageShow: () => {
      void requestForegroundRefresh(activateForeground())
      chatRuntime.resumeForeground()
    },
    onHidden: () => {
      deactivateForeground()
      partyRuntime.clearRetryTimer()
      chatRuntime.clearRefreshTimers()
    },
    onVisible: () => {
      void requestForegroundRefresh(activateForeground())
      chatRuntime.resumeForeground()
    },
  })

  activityReconcileQueue = createPlayerActivityReconcileQueue({
    active: () => (
      foregroundActive &&
      options.authenticated() &&
      Boolean(options.steamID())
    ),
    reconcile: reconcileActivity,
    onExhausted: () => presenceRuntime.reconnectEventSource(),
  })

  const stopSessionWatch = watch(
    [options.authenticated, options.steamID, chatRuntime.chatCapabilitiesKey],
    ([authenticated, steamID], previous) => {
      const previousAuthenticated = previous?.[0] ?? false
      const previousSteamID = previous?.[1] ?? ''
      if (!authenticated || !steamID) {
        stopPlayerSession(previousAuthenticated)
        resetState()
        return
      }
      if (!previousAuthenticated || previousSteamID !== steamID) {
        startPlayerSession()
        return
      }
      chatRuntime.handleCapabilitiesChanged()
    },
    { immediate: true, flush: 'sync' },
  )

  onScopeDispose(() => {
    stopSessionWatch()
    stopPlayerSession(options.authenticated())
    activityReconcileQueue.dispose()
  })

  function startPlayerSession(): void {
    sessionGeneration += 1
    presenceRuntime.stop(false)
    partyRuntime.stop()
    resetState()
    const activationGeneration = activateForeground()
    presenceRuntime.start()
    void requestForegroundRefresh(activationGeneration)
    chatRuntime.resumeForeground()
  }

  function stopPlayerSession(releasePresence: boolean): void {
    sessionGeneration += 1
    activityReconcileQueue.reset()
    presenceRuntime.stop(releasePresence)
    deactivateForeground()
    partyRuntime.stop()
    chatRuntime.stop()
  }

  function resetState(): void {
    activityReconcileQueue.reset()
    socialActionRequestID += 1
    socialState.value = null
    socialLoading.value = false
    socialActionPending.value = false
    socialError.value = null
    foregroundActivationGeneration += 1
    foregroundActive = false
    foregroundRefreshRequested = null
    foregroundRefreshCompletedKey = ''
    foregroundRefreshInFlight = null
    resetIncomingNotifications()
    partyRuntime.reset()
    chatRuntime.reset()
    presenceRuntime.reset()
  }

  async function refreshAllState(
    forceChatReplace: boolean,
    activationGeneration: number,
    refreshKey: string,
  ): Promise<boolean> {
    const generation = session.generation()
    const actorSteamID = options.steamID()
    await Promise.allSettled([
      partyRuntime.refreshParty(),
      refreshSocial(),
    ])
    if (!isCurrentForegroundRefresh(
      activationGeneration,
      generation,
      actorSteamID,
      refreshKey,
    )) {
      return false
    }
    chatRuntime.settleInitialSelection()
    if (!isCurrentForegroundRefresh(
      activationGeneration,
      generation,
      actorSteamID,
      refreshKey,
    )) {
      return false
    }
    await chatRuntime.refreshChat(forceChatReplace)
    const current = isCurrentForegroundRefresh(
      activationGeneration,
      generation,
      actorSteamID,
      refreshKey,
    )
    if (current) chatMessageNotificationsReady = true
    return current
  }

  function activateForeground(): number {
    if (!foregroundActive) {
      foregroundActive = true
      foregroundActivationGeneration += 1
    }
    return foregroundActivationGeneration
  }

  function deactivateForeground(): void {
    foregroundActive = false
    suspendIncomingNotifications()
  }

  function requestForegroundRefresh(
    activationGeneration: number,
    connectionCycle = 0,
  ): Promise<void> {
    if (!isVisibleForegroundActivation(activationGeneration)) {
      return Promise.resolve()
    }
    const key = `${activationGeneration}:${connectionCycle}`
    if (foregroundRefreshCompletedKey === key) {
      return Promise.resolve()
    }
    foregroundRefreshRequested = { key, activationGeneration }
    return runForegroundRefresh()
  }

  function runForegroundRefresh(): Promise<void> {
    const requested = foregroundRefreshRequested
    if (
      !requested ||
      foregroundRefreshCompletedKey === requested.key ||
      !isVisibleForegroundActivation(requested.activationGeneration)
    ) {
      return Promise.resolve()
    }
    if (foregroundRefreshInFlight?.key === requested.key) {
      return foregroundRefreshInFlight.promise
    }

    const promise = refreshAllState(
      true,
      requested.activationGeneration,
      requested.key,
    ).then(completed => {
      if (completed) foregroundRefreshCompletedKey = requested.key
    })
    const tracked = promise.finally(() => {
      if (foregroundRefreshInFlight?.promise === tracked) {
        foregroundRefreshInFlight = null
      }
    })
    foregroundRefreshInFlight = { key: requested.key, promise: tracked }
    return tracked
  }

  function isVisibleForegroundActivation(
    activationGeneration: number,
  ): boolean {
    return (
      foregroundActive &&
      activationGeneration === foregroundActivationGeneration &&
      options.authenticated() &&
      Boolean(options.steamID()) &&
      (typeof document === 'undefined' || !document.hidden)
    )
  }

  function isCurrentForegroundRefresh(
    activationGeneration: number,
    generation: number,
    actorSteamID: string,
    refreshKey: string,
  ): boolean {
    return (
      isVisibleForegroundActivation(activationGeneration) &&
      session.isCurrentActor(generation, actorSteamID) &&
      foregroundRefreshRequested?.key === refreshKey
    )
  }

  async function refreshSocial(): Promise<void> {
    if (!options.authenticated()) return
    const generation = session.generation()
    const actorSteamID = options.steamID()
    if (!actorSteamID) return
    const requestID = ++socialRequestID
    socialLoading.value = true
    try {
      const state = await playerApi.socialState()
      if (!session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        socialRequestID,
      )) {
        return
      }
      commitSocialState(
        state,
        pendingSocialNotificationObservedAt,
      )
      socialError.value = null
    } catch (error) {
      if (!session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        socialRequestID,
      )) {
        return
      }
      session.handleRequestError(error, socialError)
    } finally {
      if (session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        socialRequestID,
      )) {
        socialLoading.value = false
      }
    }
  }

  async function reconcileActivity(): Promise<void> {
    if (!options.authenticated()) return
    const generation = session.generation()
    const actorSteamID = options.steamID()
    if (!actorSteamID) return
    const requestActivityEpoch = socialState.value?.activity_epoch
    let state: PlayerSocialState
    try {
      state = await playerApi.socialState()
    } catch (error) {
      if (session.isCurrentActor(generation, actorSteamID)) {
        const ignoredActivityError = { value: null as string | null }
        session.handleRequestError(error, ignoredActivityError)
      }
      throw error
    }
    if (!session.isCurrentActor(generation, actorSteamID)) return
    const currentActivityEpoch = socialState.value?.activity_epoch
    if (
      currentActivityEpoch !== requestActivityEpoch &&
      state.activity_epoch !== currentActivityEpoch
    ) {
      return
    }
    commitActivityProjection(state)
  }

  function commitActivityProjection(state: PlayerSocialState): void {
    assertActivityWatermark(state)
    const currentState = socialState.value
    if (!currentState) {
      commitSocialState(state)
      return
    }
    if (
      currentState.activity_epoch === state.activity_epoch &&
      /^\d+$/.test(currentState.activity_revision) &&
      compareDecimalIDs(
        state.activity_revision,
        currentState.activity_revision,
      ) <= 0
    ) {
      return
    }
    socialState.value = applyActivityProjection(currentState, state)
  }

  function assertActivityWatermark(state: PlayerSocialState): void {
    if (
      typeof state.activity_epoch !== 'string' ||
      !state.activity_epoch ||
      typeof state.activity_revision !== 'string' ||
      !/^\d+$/.test(state.activity_revision)
    ) {
      throw new Error('invalid player activity snapshot')
    }
  }

  function applyActivityProjection(
    base: PlayerSocialState,
    activitySource: PlayerSocialState,
  ): PlayerSocialState {
    const activityByFriendship = new Map(
      activitySource.friends.map(friend => [friend.id, friend]),
    )
    return {
      ...base,
      activity_epoch: activitySource.activity_epoch,
      activity_revision: activitySource.activity_revision,
      self_game_activity: activitySource.self_game_activity,
      friends: base.friends.map(friend => {
        const activityFriend = activityByFriendship.get(friend.id)
        return {
          ...friend,
          game_activity: (
            activityFriend?.player.steam_id === friend.player.steam_id
              ? activityFriend.game_activity
              : null
          ),
        }
      }),
    }
  }

  function preserveNewerActivityProjection(
    state: PlayerSocialState,
  ): PlayerSocialState {
    const currentState = socialState.value
    if (
      !currentState ||
      currentState.activity_epoch !== state.activity_epoch ||
      !/^\d+$/.test(currentState.activity_revision) ||
      !/^\d+$/.test(state.activity_revision) ||
      compareDecimalIDs(
        currentState.activity_revision,
        state.activity_revision,
      ) <= 0
    ) {
      return state
    }
    return applyActivityProjection(state, currentState)
  }

  function commitSocialState(
    state: PlayerSocialState,
    notificationObservedAt?: number,
  ): void {
    state = preserveNewerActivityProjection(state)
    const nextIncomingRequestIDs = new Set(
      state.incoming_requests.map(request => request.id),
    )
    const addedRequestIDs = [...nextIncomingRequestIDs].filter(
      requestID => !knownIncomingFriendRequestIDs.has(requestID),
    )
    knownIncomingFriendRequestIDs = nextIncomingRequestIDs
    const canNotify = notificationsCanPlay()
    if (!friendRequestNotificationsReady) {
      friendRequestNotificationsReady = canNotify
    } else if (canNotify && addedRequestIDs.length > 0) {
      dispatchIncomingSocialNotification(
        'friend',
        addedRequestIDs,
        notificationObservedAt,
      )
    }
    if (
      notificationObservedAt !== undefined &&
      pendingSocialNotificationObservedAt === notificationObservedAt
    ) {
      pendingSocialNotificationObservedAt = undefined
    }
    socialState.value = state
    chatRuntime.reconcileDirectChatsWithSocialState(state)
  }

  function handleIncomingPartyInvitesCommitted(
    invites: readonly PlayerPartyInvite[],
    notificationObservedAt?: number,
  ): void {
    const nextInviteIDs = new Set(invites.map(invite => invite.id))
    const addedInviteIDs = [...nextInviteIDs].filter(
      inviteID => !knownIncomingPartyInviteIDs.has(inviteID),
    )
    knownIncomingPartyInviteIDs = nextInviteIDs
    const canNotify = notificationsCanPlay()
    if (!partyInviteNotificationsReady) {
      partyInviteNotificationsReady = canNotify
    } else if (canNotify && addedInviteIDs.length > 0) {
      dispatchIncomingSocialNotification(
        'party',
        addedInviteIDs,
        notificationObservedAt,
      )
    }
  }

  function notifyIncomingChatMessage(
    hint: PlayerChatChangedHint | null,
    observedAt: number,
  ): void {
    const actorSteamID = options.steamID()
    if (
      !hint ||
      hint.messageType === 'system' ||
      !chatMessageNotificationsReady ||
      !notificationsCanPlay() ||
      (hint.kind !== 'party' && hint.kind !== 'dm') ||
      hint.senderSteamID === actorSteamID
    ) {
      return
    }
    const knownConversation = chatRuntime.conversations.value.find(
      item => item.id === hint.conversationID,
    )
    if (
      knownConversation?.chat_epoch === hint.chatEpoch &&
      compareDecimalIDs(
        knownConversation.last_message_id,
        hint.messageID,
      ) >= 0
    ) {
      return
    }
    const messageKey = [
      actorSteamID,
      hint.conversationID,
      hint.chatEpoch,
      hint.messageID,
    ].join(':')
    if (seenIncomingChatMessageKeys.has(messageKey)) return
    rememberIncomingChatMessage(messageKey)
    options.onIncomingChatMessage?.(messageKey, observedAt)
  }

  function dispatchIncomingSocialNotification(
    kind: 'friend' | 'party',
    addedIDs: readonly string[],
    observedAt = Date.now(),
  ): void {
    const actorSteamID = options.steamID()
    if (!actorSteamID || addedIDs.length === 0) return
    for (const addedID of [...addedIDs].sort()) {
      options.onIncomingSocialNotification?.([
        actorSteamID,
        kind,
        addedID,
      ].join(':'), observedAt)
    }
  }

  function rememberIncomingChatMessage(messageKey: string): void {
    seenIncomingChatMessageKeys.add(messageKey)
    while (seenIncomingChatMessageKeys.size > 500) {
      const oldest = seenIncomingChatMessageKeys.values().next().value
      if (typeof oldest !== 'string') break
      seenIncomingChatMessageKeys.delete(oldest)
    }
  }

  function notificationsCanPlay(): boolean {
    return (
      foregroundActive &&
      options.authenticated() &&
      Boolean(options.steamID()) &&
      (typeof document === 'undefined' || !document.hidden)
    )
  }

  function suspendIncomingNotifications(): void {
    friendRequestNotificationsReady = false
    partyInviteNotificationsReady = false
    chatMessageNotificationsReady = false
    pendingSocialNotificationObservedAt = undefined
  }

  function resetIncomingNotifications(): void {
    suspendIncomingNotifications()
    knownIncomingFriendRequestIDs = new Set()
    knownIncomingPartyInviteIDs = new Set()
    seenIncomingChatMessageKeys.clear()
  }

  function earliestObservedAt(
    current: number | undefined,
    candidate: number,
  ): number {
    return current === undefined
      ? candidate
      : Math.min(current, candidate)
  }

  async function sendFriendRequest(steamID: string): Promise<boolean> {
    return runSocialAction(async isCurrent => {
      await playerApi.sendFriendRequest(steamID)
      if (!isCurrent()) return
      await refreshSocial()
    })
  }

  async function acceptFriendRequest(requestID: string): Promise<boolean> {
    return runSocialAction(async isCurrent => {
      await playerApi.acceptFriendRequest(requestID)
      if (!isCurrent()) return
      await refreshSocial()
    })
  }

  async function removeFriendRequest(requestID: string): Promise<boolean> {
    return runSocialAction(async isCurrent => {
      await playerApi.removeFriendRequest(requestID)
      if (!isCurrent()) return
      await refreshSocial()
    })
  }

  async function removeFriendship(friendshipID: string): Promise<boolean> {
    const removedSteamID = socialState.value?.friends.find(
      friend => friend.id === friendshipID,
    )?.player.steam_id
    return runSocialAction(async isCurrent => {
      await playerApi.removeFriendship(friendshipID)
      if (!isCurrent()) return
      await refreshSocial()
      if (!isCurrent() || !removedSteamID) return
      chatRuntime.removeFriendDirectChat(removedSteamID)
    })
  }

  async function unblockPlayer(blockID: string): Promise<boolean> {
    return runSocialAction(async isCurrent => {
      await playerApi.unblockPlayer(blockID)
      if (!isCurrent()) return
      await refreshSocial()
    })
  }

  async function ignorePlayerNotifications(
    steamID: string,
  ): Promise<boolean> {
    const targetSteamID = steamID.trim()
    return runSocialAction(async isCurrent => {
      const response = await playerApi.ignorePlayerNotifications(targetSteamID)
      if (!isCurrent()) return
      const currentState = socialState.value
      if (currentState) {
        commitSocialState({
          ...currentState,
          incoming_requests: currentState.incoming_requests.filter(
            request => request.player.steam_id !== targetSteamID,
          ),
          notification_ignores: [
            ...(currentState.notification_ignores ?? []).filter(
              ignore => ignore.player.steam_id !== targetSteamID,
            ),
            response.notification_ignore,
          ],
        })
      }
      partyRuntime.removeInvitesByInviter(targetSteamID)
      if (!isCurrent()) return
      await Promise.all([
        refreshSocial(),
        partyRuntime.refreshParty(),
      ])
    })
  }

  async function unignorePlayerNotifications(
    ignoreID: string,
  ): Promise<boolean> {
    return runSocialAction(async isCurrent => {
      await playerApi.unignorePlayerNotifications(ignoreID)
      if (!isCurrent()) return
      const currentState = socialState.value
      if (currentState) {
        commitSocialState({
          ...currentState,
          notification_ignores: (
            currentState.notification_ignores ?? []
          ).filter(ignore => ignore.id !== ignoreID),
        })
      }
      await Promise.all([
        refreshSocial(),
        partyRuntime.refreshParty(),
      ])
    })
  }

  async function runSocialAction(
    action: (isCurrent: () => boolean) => Promise<void>,
  ): Promise<boolean> {
    if (socialActionPending.value) return false
    const generation = session.generation()
    const actorSteamID = options.steamID()
    const requestID = ++socialActionRequestID
    const isCurrent = () => session.isCurrentActorRequest(
      generation,
      actorSteamID,
      requestID,
      socialActionRequestID,
    )
    socialActionPending.value = true
    socialError.value = null
    try {
      await action(isCurrent)
      if (!isCurrent()) return false
      return true
    } catch (error) {
      if (!isCurrent()) return false
      session.handleRequestError(error, socialError)
      return false
    } finally {
      if (isCurrent()) socialActionPending.value = false
    }
  }

  function clearSocialError(): void {
    socialError.value = null
  }

  return {
    party: partyRuntime.party,
    partyETag: partyRuntime.partyETag,
    incomingPartyInvites: partyRuntime.incomingPartyInvites,
    socialState,
    conversations: chatRuntime.conversations,
    conversation: chatRuntime.conversation,
    messages: chatRuntime.messages,
    activeChatKind: chatRuntime.activeChatKind,
    selectedDM: chatRuntime.selectedDM,
    recentDMConversations: chatRuntime.recentDMConversations,
    activeChatTargetKey: chatRuntime.activeChatTargetKey,
    partyLoading: partyRuntime.partyLoading,
    partyStateHealthy: partyRuntime.partyStateHealthy,
    socialLoading,
    chatLoading: chatRuntime.chatLoading,
    partyActionPending: partyRuntime.partyActionPending,
    socialActionPending,
    chatSending: chatRuntime.chatSending,
    partyError: partyRuntime.partyError,
    socialError,
    chatError: chatRuntime.chatError,
    chatNotice: chatRuntime.chatNotice,
    failedSendDraftTargetKey: chatRuntime.failedSendDraftTargetKey,
    partyInviteCooldownRetry: partyRuntime.partyInviteCooldownRetry,
    streamConnected: presenceRuntime.streamConnected,
    isPartyLeader: partyRuntime.isPartyLeader,
    refreshParty: partyRuntime.refreshParty,
    refreshSocial,
    refreshChat: chatRuntime.refreshChat,
    leaveParty: partyRuntime.leaveParty,
    disbandParty: partyRuntime.disbandParty,
    inviteFriendToParty: partyRuntime.inviteFriendToParty,
    acceptPartyInvite: partyRuntime.acceptPartyInvite,
    declinePartyInvite: partyRuntime.declinePartyInvite,
    declineAllPartyInvites: partyRuntime.declineAllPartyInvites,
    kickPartyMember: partyRuntime.kickPartyMember,
    transferPartyLeadership: partyRuntime.transferPartyLeadership,
    sendFriendRequest,
    acceptFriendRequest,
    removeFriendRequest,
    removeFriendship,
    unblockPlayer,
    ignorePlayerNotifications,
    unignorePlayerNotifications,
    selectChatChannel: chatRuntime.selectChatChannel,
    selectDirectChat: chatRuntime.selectDirectChat,
    hideDirectChatFromRecents: chatRuntime.hideDirectChatFromRecents,
    sendMessage: chatRuntime.sendMessage,
    markLatestMessageRead: chatRuntime.markLatestMessageRead,
    reportChatMessage: chatRuntime.reportChatMessage,
    clearFailedSendDraftRecovery: chatRuntime.clearFailedSendDraftRecovery,
    clearPartyError: partyRuntime.clearError,
    clearSocialError,
    clearChatFeedback: chatRuntime.clearFeedback,
  }
}
